/**
 * services/api.js
 * -----------------------------------------------------------------------------
 * Centralized Axios client and a tiny `auth` helper that persists the JWT in
 * localStorage. All other modules import { api } and the typed helpers below
 * — there's no direct axios usage anywhere else.
 */

import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const STORAGE_KEY = 'lead_scoring_auth';

export const api = axios.create({
    baseURL: BASE_URL,
    headers: { 'Content-Type': 'application/json' },
});

// --- Token management -------------------------------------------------------

export const auth = {
    get() {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    },
    set(payload) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    },
    clear() {
        localStorage.removeItem(STORAGE_KEY);
    },
    token() {
        return this.get()?.token || null;
    },
    user() {
        return this.get()?.user || null;
    },
    isLoggedIn() {
        return !!this.token();
    },
};

// Inject the JWT on every request.
api.interceptors.request.use((config) => {
    const token = auth.token();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// On a 401 from anywhere, drop the saved auth and bounce to /login.
api.interceptors.response.use(
    (resp) => resp,
    (error) => {
        if (error.response?.status === 401) {
            auth.clear();
            // Avoid redirect loops on the auth pages themselves.
            const path = window.location.pathname;
            if (path !== '/login' && path !== '/signup') {
                window.location.assign('/login');
            }
        }
        return Promise.reject(error);
    },
);

/** Pull the most useful error message out of an axios error. */
export function errorMessage(err) {
    return (
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong.'
    );
}

// ---------------------------------------------------------------------------
// Typed-ish API helpers
// ---------------------------------------------------------------------------

export const AuthAPI = {
    signup: (data) => api.post('/auth/signup', data).then((r) => r.data),
    login:  (data) => api.post('/auth/login',  data).then((r) => r.data),
    logout: ()     => api.post('/auth/logout').then((r) => r.data),
    me:     ()     => api.get('/auth/me').then((r) => r.data),
};

export const LeadsAPI = {
    list:        (params) => api.get('/leads', { params }).then((r) => r.data),
    get:         (id)     => api.get(`/leads/${id}`).then((r) => r.data),
    create:      (data)   => api.post('/leads', data).then((r) => r.data),
    update:      (id, p)  => api.put(`/leads/${id}`, p).then((r) => r.data),
    remove:      (id)     => api.delete(`/leads/${id}`).then((r) => r.data),
    analyze:     (leadId) => api.post('/leads/analyze', { leadId }).then((r) => r.data),
    uploadCsv:   (file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post('/leads', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }).then((r) => r.data);
    },
};

export const ReportsAPI = {
    daily:     () => api.get('/reports/daily').then((r) => r.data),
    history:   () => api.get('/reports/history').then((r) => r.data),
    emailNow:  () => api.post('/reports/email-now').then((r) => r.data),
    closeRate: () => api.get('/reports/close-rate').then((r) => r.data),
};

export const IntegrationsAPI = {
    list:             ()         => api.get('/integrations').then((r) => r.data),
    getAuthorizeUrl:  (provider) => api.get(`/integrations/${provider}/authorize`).then((r) => r.data),
    disconnect:       (id)       => api.delete(`/integrations/${id}`).then((r) => r.data),
    setupTypeform:    ()         => api.get('/integrations/typeform/setup').then((r) => r.data),
};
